import React, { useEffect, useState } from 'react';
import { Box, Button, Collapsible, Flex, Input as NativeInput } from '@chakra-ui/react';
import { Context } from '../../App';
import { useMutation } from 'react-query';
import Card from '../../base-components/Card';
import { clearCompanySecret, setCompanySecret, updateCompanySettings } from '../../api/company-setting-controller';
import CompanySetting, { COMPANY_SECRET_KEYS, CompanySecretKey } from '../../model/company-setting';
import Input from '../../base-components/Input';
import Text from '../../base-components/Text';
import useToast from '../../base-components/hooks/useToast';
import { SPACING } from '../../generic/config';

type PublicFields = Pick<CompanySetting, "name" | "mapboxToken">;

const CompanySettings = () => {
    const { translations, companySettings } = React.useContext(Context);
    const t = translations.model.companySettings;
    const saved: PublicFields = { name: companySettings?.data?.name ?? "", mapboxToken: companySettings?.data?.mapboxToken ?? "" };
    const [current, setCurrent] = useState<PublicFields>(saved);

    const { mutate: doUpdateCompanySettings } = useMutation(updateCompanySettings);

    useEffect(() => {
        setCurrent({ name: companySettings?.data?.name ?? "", mapboxToken: companySettings?.data?.mapboxToken ?? "" });
    }, [companySettings?.data]);

    const isDirty = current.name !== saved.name || current.mapboxToken !== saved.mapboxToken;
    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) =>
        setCurrent(prev => ({ ...prev, [e.target.name]: e.target.value }));

    return (
        <>
            <Card title={translations.systemSettings.companySettings.title} description={translations.systemSettings.companySettings.description}>
                <Input disabled title={t.id} name="id" defaultValue={companySettings?.data?.id} />
                <Input title={t.name} name="name" key={`name-${saved.name}`} defaultValue={saved.name} onChange={handleInput}
                    autoComplete="off" data-1p-ignore data-lpignore="true" data-bwignore="true" data-form-type="other" />
                <Input title={t.mapboxToken} name="mapboxToken" key={`mapbox-${saved.mapboxToken}`} defaultValue={saved.mapboxToken} type="text" onChange={handleInput}
                    autoComplete="off" spellCheck={false} data-1p-ignore data-lpignore="true" data-bwignore="true" data-form-type="other" />
                <Text variant="small" color="fg.muted" mx={SPACING / 2}>{t.mapboxHint}</Text>
                <Collapsible.Root open={isDirty}>
                    <Collapsible.Content>
                        <Button mt={SPACING} onClick={() => doUpdateCompanySettings(current)}>{translations.systemSettings.companySettings.save}</Button>
                    </Collapsible.Content>
                </Collapsible.Root>
            </Card>
            <Card title={t.secretsTitle} description={t.secretsDescription}>
                {COMPANY_SECRET_KEYS.map(key => <SecretRow key={key} secretKey={key} />)}
            </Card>
        </>
    );
};

/** Write-only: shows whether the key is set, lets an admin replace or remove it. */
const SecretRow = ({ secretKey }: { secretKey: CompanySecretKey }) => {
    const { translations, companySettings } = React.useContext(Context);
    const t = translations.model.companySettings;
    const toast = useToast();
    const status = companySettings?.data?.secrets?.[secretKey];
    const [value, setValue] = useState("");
    const [busy, setBusy] = useState(false);
    const labels: Record<CompanySecretKey, string> = { geocodingToken: t.geocodingToken };

    const run = async (action: () => Promise<void>, done: string) => {
        setBusy(true);
        try {
            await action();
            setValue("");
            toast(done, "", "success");
        } catch {
            // The API error toast explains it.
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box m={SPACING / 4} textAlign="start" data-testid={`secret-${secretKey}`}>
            <Flex gap={SPACING} alignItems="baseline" mb={SPACING / 4}>
                <Text weight="Bold" ml={SPACING / 2}>{labels[secretKey]}</Text>
                <Text variant="small" color={status?.set ? undefined : "fg.muted"} data-testid={`secret-${secretKey}-status`}>
                    {status?.set ? t.secretSet(status.hint) : t.secretNotSet}
                </Text>
            </Flex>
            <Flex gap={SPACING} direction={{ base: "column", md: "row" }}>
                {/* Not type="password": browsers then treat the page as a login form and fill in the
                    user's own username/password. Masked text + autofill opt-outs instead. */}
                <NativeInput
                    type="text"
                    name={`api-key-${secretKey}`}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    data-1p-ignore
                    data-lpignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                    style={{ WebkitTextSecurity: value ? "disc" : "none" } as React.CSSProperties}
                    placeholder={t.secretPlaceholder}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    data-testid={`secret-${secretKey}-input`}
                />
                <Button disabled={busy || value.trim() === ""} onClick={() => run(() => setCompanySecret(secretKey, value.trim()), t.secretSaved)}>
                    {t.secretSave}
                </Button>
                {status?.set && (
                    <Button variant="outline" disabled={busy} onClick={() => run(() => clearCompanySecret(secretKey), t.secretRemoved)}>
                        {t.secretRemove}
                    </Button>
                )}
            </Flex>
        </Box>
    );
};

export default CompanySettings;
